import { showToast } from '@lokalise/louis';
import type { AxiosResponse } from 'axios';
import type { SagaIterator } from 'redux-saga';
import { all, debounce, put, takeEvery } from 'redux-saga/effects';

import tasksApi from '@app/api/tasks';
import bugsnag from '@app/utils/bugsnag';
import downloadBlob from '@app/utils/downloadBlob';
import formatDateWithTimeZone from '@app/utils/formatDateWithTimeZone/formatDateWithTimeZone';

import { badgeActions } from '../../store/badges/reducers';
import {
    closeLanguageCall,
    closeTaskCall,
    deleteTaskCall,
    editContributorsCall,
    getContributorsCall,
    getDetailedReportCall,
    getFilterOptionsCall,
    getFinalReportCall,
    getInitialReportCall,
    getLqaReportCall,
    getOfflineXLIFFCall,
    getQueriedTasksCall,
    getTaskTagCall,
    postOfflineXLIFFCall,
    putTaskCall,
} from './api';
import { tasksActions } from './reducers';
import type { GetTasks } from './types';

const getCurrentTime = () => formatDateWithTimeZone(new Date(), 'yyyy-MM-dd_H-mm-ss');

function* getTasksWatcher({ payload }: ReturnType<typeof tasksActions.getTasks>) {
    try {
        const { projectId, branch } = payload;

        yield put(tasksActions.setTasksLoading(true));

        const [{ data: queued }, { data: ongoing }, { data: closed }, { data: filterOptions }] = yield all([
            getQueriedTasksCall({ projectId, query: 'queued', branch }),
            getQueriedTasksCall({ projectId, query: 'ongoing', branch }),
            getQueriedTasksCall({ projectId, query: 'closed', branch }),
            getFilterOptionsCall(projectId),
        ]);

        yield put(tasksActions.setTasks({ ongoing, queued, closed }));
        yield put(tasksActions.setTasksFilterOptions(filterOptions));
    } catch (e) {
        bugsnag.log(`Failed to get tasks. ProjectId: ${payload.projectId} Error: ${e}`);

        showToast({ type: 'error', title: 'Something went wrong, please contact our support!' });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* getQueriedTasksWatcher({ payload }: ReturnType<typeof tasksActions.getQueriedTasks>) {
    try {
        const { projectId, query, page, pageSize, branch, search, filters } = payload;

        yield put(tasksActions.setTasksLoading(true));

        const { data }: AxiosResponse<GetTasks> = yield getQueriedTasksCall({
            projectId,
            query,
            page,
            pageSize,
            branch,
            search,
            filters,
        });

        yield put(tasksActions.setQueriedTasks({ content: data.content, status: query, page }));
    } catch (e) {
        bugsnag.log(`Failed to get queried tasks. ProjectId: ${payload.projectId} Error: ${e}`);

        showToast({ type: 'error', title: 'Something went wrong, please contact our support!' });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* getTaskTagWatcher({ payload }: ReturnType<typeof tasksActions.getTasks>) {
    try {
        const { projectId } = payload;
        yield put(tasksActions.setTaskTagsLoading(true));

        const { data }: AxiosResponse<{ tags: string[] }> = yield getTaskTagCall(projectId);
        yield put(tasksActions.setTaskTags(data.tags));
    } catch (e) {
        bugsnag.log(`Failed to get tasks tags. ProjectId: ${payload.projectId} Error: ${e}`);

        showToast({ type: 'error', title: 'Something went wrong, please contact our support!' });
    } finally {
        yield put(tasksActions.setTaskTagsLoading(false));
    }
}

function* putTaskWatcher({ payload }: ReturnType<typeof tasksActions.putTask>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        yield putTaskCall(payload.editData);
        yield put(tasksActions.editTask(payload));
        showToast({ type: 'success', title: 'You have successfully edited the task.' });
    } catch (e) {
        bugsnag.log(`Failed to edit task. ProjectId: ${payload.editData.projectId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to edit the task, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* closeTaskWatcher({ payload }: ReturnType<typeof tasksActions.closeTask>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        const { statusLength, statusTotal, taskId, status, projectId, branch, branchId } = payload;

        yield closeTaskCall(taskId);

        // if last task in list force to load more if have more Tasks
        const hasLoadMore = statusTotal > statusLength;

        yield put(tasksActions.setCloseTask(payload));
        yield put(badgeActions.getBadgeStatus({ projectId, branchId }));
        yield put(tasksApi.util.invalidateTags([{ type: 'TASKS_BADGE', id: 'LIST' }]));

        if (statusLength === 1 && hasLoadMore) {
            const { data } = yield getQueriedTasksCall({ projectId, query: status, branch });
            yield put(tasksActions.setTasks({ [status]: data }));
        }

        showToast({ type: 'success', title: 'You have successfully closed the task.' });
    } catch (e) {
        bugsnag.log(`Failed to close task. ProjectId: ${payload.projectId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to close the task, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* closeLanguageWatcher({ payload }: ReturnType<typeof tasksActions.closeLanguage>) {
    try {
        const { taskId, langId } = payload;

        yield put(tasksActions.setTasksLoading(true));

        yield closeLanguageCall(taskId, langId);
        yield put(tasksActions.setCloseLanguage(payload));
        showToast({ type: 'success', title: 'You have successfully closed the language.' });
    } catch (e) {
        bugsnag.log(`Failed to close language. TaskId: ${payload.taskId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to close the language, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* deleteTaskWatcher({ payload }: ReturnType<typeof tasksActions.deleteTask>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        const { statusLength, statusTotal, taskId, status, projectId, branch, branchId } = payload;

        yield deleteTaskCall(taskId);

        // if last task in list force to load more if have more Tasks
        const hasLoadMore = statusTotal > statusLength;

        yield put(badgeActions.getBadgeStatus({ projectId, branchId }));
        yield put(tasksApi.util.invalidateTags([{ type: 'TASKS_BADGE', id: 'LIST' }]));
        yield put(tasksActions.setDeleteTask(payload));

        if (statusLength === 1 && hasLoadMore) {
            const { data } = yield getQueriedTasksCall({ projectId, query: status, branch });
            yield put(tasksActions.setTasks({ [status]: data }));
        }

        showToast({ type: 'success', title: 'You have successfully deleted the task.' });
    } catch (e) {
        bugsnag.log(`Failed to delete task. ProjectId: ${payload.projectId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to delete the task, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* initialReportWatcher({ payload }: ReturnType<typeof tasksActions.initialReport>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        const { data } = yield getInitialReportCall(payload);
        downloadBlob(data, `Task-initial-analysis-${getCurrentTime()}.xlsx`);
        showToast({ type: 'success', title: 'You have successfully downloaded the Initial Analysis.' });
    } catch (e) {
        bugsnag.log(`Failed to create initial report. TaskId: ${payload} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to download the Initial Analysis, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* finalReportWatcher({ payload }: ReturnType<typeof tasksActions.finalReport>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        const { data } = yield getFinalReportCall(payload);
        downloadBlob(data, `Task-final-report-${getCurrentTime()}.xlsx`);
        showToast({ type: 'success', title: 'You have successfully downloaded the Final Report.' });
    } catch (e) {
        bugsnag.log(`Failed to create final report. TaskId: ${payload} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to download Final Report, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* detailReportWatcher({ payload }: ReturnType<typeof tasksActions.finalReport>) {
    try {
        yield put(tasksActions.setTasksLoading(true));
        const { data } = yield getDetailedReportCall(payload);
        downloadBlob(data, `Task_translation_report-${getCurrentTime()}.csv`);
        showToast({ type: 'success', title: 'You have successfully downloaded the Detailed Translation Report.' });
    } catch (e) {
        bugsnag.log(`Failed to create detailed report. TaskId: ${payload} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to download Detailed Translation Report, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* uploadOfflineXLIFFWatcher({ payload }: ReturnType<typeof tasksActions.uploadOfflineXLIFF>) {
    try {
        yield put(tasksActions.setTasksLoading(true));
        yield postOfflineXLIFFCall(payload.taskId, payload.files).then((response: AxiosResponse) => {
            response.data.fileStatuses.forEach((fileStatus: { fileName: string; error: string }) => {
                let title = `Successfully uploaded ${fileStatus.fileName}`;

                if (fileStatus.error) {
                    title = `Failed to process ${fileStatus.fileName}`;
                }

                showToast({
                    type: fileStatus.error ? 'error' : 'success',
                    title,
                    description: fileStatus.error,
                });
            });
        });
    } catch (e) {
        bugsnag.log(`Failed to upload OfflineXLIFF report. TaskId: ${payload.taskId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to upload XLIFF file(-s), please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* downloadOfflineXLIFFWatcher({ payload }: ReturnType<typeof tasksActions.downloadOfflineXLIFF>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        const { data } = yield getOfflineXLIFFCall(payload);

        window.location.assign(data.url);

        showToast({ type: 'success', title: 'You have successfully downloaded task scope as XLIFF file(-s).' });
    } catch (e) {
        bugsnag.log(`Failed to create detailed report. TaskId: ${payload} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to download task scope as XLIFF file(-s), please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* getContributorsWatcher({ payload }: ReturnType<typeof tasksActions.getContributors>) {
    try {
        yield put(tasksActions.setContributorsLoading(true));

        const { data } = yield getContributorsCall(payload);
        const [landIg] = payload.langIds;

        yield put(tasksActions.setContributors(data[landIg]));
    } catch (e) {
        bugsnag.log(`Failed to get contributors. ProjectId: ${payload.projectId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to fetch contributors, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setContributorsLoading(false));
    }
}

function* editContributorsWatcher({ payload }: ReturnType<typeof tasksActions.editContributors>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        yield editContributorsCall(payload);
        yield put(tasksActions.setLanguageContributors(payload));

        showToast({ type: 'success', title: 'You have successfully edited assignees.' });
    } catch (e) {
        bugsnag.log(`Failed to edit contributors. TaskId: ${payload.taskId} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to edit contributors, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

function* getSearchedOrFilteredTasksWatcher({ payload }: ReturnType<typeof tasksActions.getSearchedOrFilteredTasks>) {
    const { search, branch, projectId, filters } = payload;

    try {
        yield put(tasksActions.setSearchOrFilterTasksLoading(true));
        yield put(tasksActions.setSearchOrFilterResultsShown(!!search || !!filters.length));

        const [{ data: queued }, { data: ongoing }, { data: closed }] = yield all([
            getQueriedTasksCall({ projectId, query: 'queued', branch, search, filters }),
            getQueriedTasksCall({ projectId, query: 'ongoing', branch, search, filters }),
            getQueriedTasksCall({ projectId, query: 'closed', branch, search, filters }),
        ]);

        if (filters) {
            yield put(tasksActions.setTasksFilterValue(filters));
        }

        if (search !== undefined) {
            yield put(tasksActions.setTasksSearchValue(search));
        }

        yield put(tasksActions.setTasks({ ongoing, queued, closed }));
    } catch (e) {
        showToast({
            type: 'error',
            title: 'Failed to search or filter tasks, please contact support via chat or hello@lokalise.com',
        });
        bugsnag.log(`Failed to search or filter tasks. TaskId: ${projectId} Error: ${e}`);
    } finally {
        yield put(tasksActions.setSearchOrFilterTasksLoading(false));
    }
}

function* lqaReportWatcher({ payload }: ReturnType<typeof tasksActions.downloadLqaReport>) {
    try {
        yield put(tasksActions.setTasksLoading(true));

        const { data } = yield getLqaReportCall(payload);
        downloadBlob(data, `Task-ai-assisted-lqa-${getCurrentTime()}.xlsx`);
        showToast({ type: 'success', title: 'You have successfully downloaded the AI-Assisted LQA report.' });
    } catch (e) {
        bugsnag.log(`Failed to create AI-Assisted LQA report. TaskId: ${payload} Error: ${e}`);

        showToast({
            type: 'error',
            title: 'Failed to download the AI-Assisted LQA report, please contact support via chat or hello@lokalise.com',
        });
    } finally {
        yield put(tasksActions.setTasksLoading(false));
    }
}

export default function* initTasksSagas(): SagaIterator {
    yield takeEvery(tasksActions.getTasks.match, getTasksWatcher);
    yield takeEvery(tasksActions.getQueriedTasks.match, getQueriedTasksWatcher);
    yield takeEvery(tasksActions.getTaskTags.match, getTaskTagWatcher);
    yield takeEvery(tasksActions.putTask.match, putTaskWatcher);
    yield takeEvery(tasksActions.closeTask.match, closeTaskWatcher);
    yield takeEvery(tasksActions.closeLanguage.match, closeLanguageWatcher);
    yield takeEvery(tasksActions.deleteTask.match, deleteTaskWatcher);
    yield takeEvery(tasksActions.initialReport.match, initialReportWatcher);
    yield takeEvery(tasksActions.finalReport.match, finalReportWatcher);
    yield takeEvery(tasksActions.detailedReport.match, detailReportWatcher);
    yield takeEvery(tasksActions.uploadOfflineXLIFF.match, uploadOfflineXLIFFWatcher);
    yield takeEvery(tasksActions.downloadOfflineXLIFF.match, downloadOfflineXLIFFWatcher);
    yield takeEvery(tasksActions.getContributors.match, getContributorsWatcher);
    yield takeEvery(tasksActions.editContributors.match, editContributorsWatcher);
    yield debounce(500, tasksActions.getSearchedOrFilteredTasks.match, getSearchedOrFilteredTasksWatcher);
    yield takeEvery(tasksActions.downloadLqaReport.match, lqaReportWatcher);
}
